/**
 * NotificationsPage - View all notifications
 *
 * Phase 3.1b - React Query as single source of truth
 *
 * Features:
 * - Infinite scroll pagination via useNotificationsInfinite hook
 * - React Query caches notifications list (no local useState)
 * - Filter by notification type
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Bell, User, Award, Check, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import {
  useNotificationsInfinite,
  type Notification,
} from '../hooks/useNotificationsQuery';
import { useBackNavigation } from '../hooks/useBackNavigation';
import { formatDistanceToNow } from '../utils/dateUtils';

export function NotificationsPage() {
  const navigate = useNavigate();
  const goBack = useBackNavigation();
  const { user } = useAuth();
  const { markAsRead, markAllAsRead } = useNotifications();
  const [filter, setFilter] = useState<string | null>(null);

  // React Query hook - single source of truth for notifications list
  const {
    notifications,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useNotificationsInfinite({ limit: 20, type: filter });

  // Handle notification click - mutation updates React Query cache
  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read (mutation updates cache optimistically)
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }

    // Navigate based on type (system notifications only - inbox types handled in /inbox)
    if (notification.type === 'new_follower' && notification.from_user?.username) {
      navigate(`/u/${notification.from_user.username}`);
    }
    // milestone notifications don't navigate anywhere
  };

  // Handle mark all as read - mutation updates React Query cache
  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  // Load more
  const handleLoadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  // Get icon for notification type (system notifications only)
  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'new_follower':
        return <User size={20} className="text-teal-400" />;
      case 'milestone':
        return <Award size={20} className="text-purple-400" />;
      default:
        return <Bell size={20} className="text-slate-400" />;
    }
  };

  // Filter options (system notifications only - inbox types handled in /inbox)
  const filterOptions = [
    { value: null, label: 'All' },
    { value: 'new_follower', label: 'Followers' },
    { value: 'milestone', label: 'Milestones' },
  ];

  const hasUnread = notifications.some((n) => !n.is_read);

  // Redirect if not logged in
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <button
            onClick={goBack}
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-3"
          >
            <ArrowLeft size={16} />
            <span className="text-sm font-medium">Back</span>
          </button>
          <p className="text-slate-400">Please sign in to view notifications</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-16">
      {/* Header */}
      <div className="bg-[#0F1419] border-b border-white/[0.06] sticky top-16 z-20">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <button
                onClick={goBack}
                className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-2"
                aria-label="Go back"
              >
                <ArrowLeft size={18} />
                <span className="text-sm font-medium">Back</span>
              </button>
              <h1 className="text-xl font-bold text-white flex items-center gap-2">
                <Bell size={24} className="text-teal-400" />
                Notifications
              </h1>
            </div>
            {hasUnread && (
              <button
                onClick={handleMarkAllAsRead}
                className="text-sm text-teal-400 hover:text-teal-300 transition-colors flex items-center gap-1"
              >
                <Check size={16} />
                Mark all read
              </button>
            )}
          </div>

          {/* Filter tabs */}
          <div className="flex gap-2 mt-4 overflow-x-auto pb-1">
            {filterOptions.map((option) => (
              <button
                key={option.value ?? 'all'}
                onClick={() => setFilter(option.value)}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg whitespace-nowrap transition-colors ${
                  filter === option.value
                    ? 'bg-teal-500/20 text-teal-400'
                    : 'text-slate-400 hover:text-white hover:bg-white/[0.06]'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={24} className="animate-spin text-slate-500" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-12">
            <Bell size={48} className="mx-auto mb-4 text-slate-600" />
            <h2 className="text-lg font-medium text-slate-300 mb-2">
              No notifications
            </h2>
            <p className="text-sm text-slate-500">
              {filter
                ? `No ${filter.replace('_', ' ')} notifications`
                : "You're all caught up!"}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-white/[0.06]">
            {notifications.map((notification) => (
              <li key={notification.id}>
                <button
                  onClick={() => handleNotificationClick(notification)}
                  className={`w-full flex items-start gap-4 px-4 py-4 text-left hover:bg-white/[0.02] transition-colors ${
                    !notification.is_read ? 'bg-teal-500/5' : ''
                  }`}
                >
                  {/* Avatar/Icon */}
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center overflow-hidden">
                    {notification.from_user?.avatar_url ? (
                      <img
                        src={notification.from_user.avatar_url}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      getNotificationIcon(notification.type)
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-200">
                      {notification.from_user && (
                        <span className="font-medium text-white">
                          {notification.from_user.display_name ||
                            notification.from_user.username}
                        </span>
                      )}{' '}
                      {notification.body}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      {formatDistanceToNow(notification.created_at)}
                    </p>
                  </div>

                  {/* Unread indicator */}
                  {!notification.is_read && (
                    <div className="flex-shrink-0 w-2 h-2 mt-2 bg-teal-400 rounded-full" />
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* Load more */}
        {hasNextPage && (
          <div className="mt-6 text-center">
            <button
              onClick={handleLoadMore}
              disabled={isFetchingNextPage}
              className="px-6 py-2 text-sm font-medium text-teal-400 hover:text-teal-300 disabled:opacity-50 transition-colors"
            >
              {isFetchingNextPage ? (
                <Loader2 size={16} className="mx-auto animate-spin" />
              ) : (
                'Load more'
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
